import { Link } from 'react-router-dom';
import { Screen } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { LinkButton } from '@/components/ui/LinkButton';
import { Card, CardTitle, EmptyState, ProgressBar, Spinner, StatCard } from '@/components/ui/Card';
import { useAppData } from '@/services/data/dataContext';
import { useAsync } from '@/hooks/useAsync';
import {
  computeSceneStatistics,
  computeShowStatistics,
  mostMissedLines,
  practiceMsThisWeek,
  weakestScenes,
} from '@/services/progress/progressService';
import { formatDuration } from '@/lib/date';
import { pluralize } from '@/lib/text';

export function DashboardScreen() {
  const { data } = useAppData();

  const dashboard = useAsync(async () => {
    const [shows, stats] = await Promise.all([data.listShows(), data.getStats()]);
    const current = shows[0] ?? null;
    if (!current) return { shows, stats, current: null, detail: null };

    const [script, progress] = await Promise.all([
      data.getScript(current.showId),
      data.getProgress(current.showId),
    ]);
    if (!script) return { shows, stats, current, detail: null };

    return {
      shows,
      stats,
      current,
      detail: {
        overall: computeShowStatistics(script, progress, current.myRoles),
        scenes: computeSceneStatistics(script, progress, current.myRoles),
        missed: mostMissedLines(script, progress, current.myRoles, 3),
      },
    };
  }, [data]);

  if (dashboard.loading) return <Spinner label="Loading your dashboard" />;

  const result = dashboard.data;
  if (!result || result.shows.length === 0) {
    return (
      <>
        <TopBar title="Line Runner" />
        <Screen>
          <EmptyState
            title="No shows yet"
            description="Add a script and pick your role to start rehearsing."
            action={<LinkButton to="/shows/new" size="lg">Add a show</LinkButton>}
          />
        </Screen>
      </>
    );
  }

  const { stats, current, detail } = result;
  const weekMs = practiceMsThisWeek(stats.dailyMs);

  return (
    <>
      <TopBar title="Dashboard" subtitle={current ? current.title : undefined} />
      <Screen>
        {current && detail ? (
          <Card className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-2">
              <CardTitle>Current show</CardTitle>
              <Link to={`/shows/${current.showId}`} className="text-brand-600 text-sm font-medium">
                Details
              </Link>
            </div>
            <p className="text-lg font-semibold">{current.title}</p>
            <ProgressBar percent={detail.overall.progressPercent} label="Memorization progress" />
            <p className="text-ink-500 dark:text-ink-400 text-sm">
              {detail.overall.memorized} of {detail.overall.totalLines}{' '}
              {pluralize(detail.overall.totalLines, 'line')} memorized ·{' '}
              {detail.overall.progressPercent}%
            </p>
            <LinkButton to={`/practice/${current.showId}`} size="lg" block>
              Start rehearsing
            </LinkButton>
          </Card>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <StatCard
            label="Daily streak"
            value={stats.currentStreak}
            hint={`Best ${stats.longestStreak} ${pluralize(stats.longestStreak, 'day')}`}
          />
          <StatCard label="This week" value={formatDuration(weekMs)} hint="Practice time" />
          <StatCard
            label="Accuracy"
            value={detail ? `${Math.round(detail.overall.accuracy * 100)}%` : '—'}
            hint={detail ? `${detail.overall.totalAttempts} attempts` : undefined}
          />
          <StatCard
            label="Practiced"
            value={detail ? detail.overall.practicedLines : '—'}
            hint={detail ? `of ${detail.overall.totalLines} lines` : undefined}
          />
        </div>

        {detail && detail.scenes.length > 0 ? (
          <Card className="flex flex-col gap-3">
            <CardTitle>Weakest scenes</CardTitle>
            {weakestScenes(detail.scenes).length === 0 ? (
              <p className="text-ink-500 dark:text-ink-400 text-sm">
                Rehearse a scene to see where you are struggling.
              </p>
            ) : (
              <ul className="flex flex-col gap-3">
                {weakestScenes(detail.scenes).map((scene) => (
                  <li key={scene.sceneId} className="flex flex-col gap-1.5">
                    <div className="flex justify-between gap-3 text-sm">
                      <span className="truncate font-medium">{scene.name}</span>
                      <span className="text-ink-500 dark:text-ink-400 tabular-nums">
                        {Math.round(scene.accuracy * 100)}%
                      </span>
                    </div>
                    <ProgressBar percent={scene.accuracy * 100} label={`${scene.name} accuracy`} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : null}

        {detail && detail.missed.length > 0 ? (
          <Card className="flex flex-col gap-3">
            <CardTitle>Most missed lines</CardTitle>
            <ul className="flex flex-col gap-3">
              {detail.missed.map((line) => (
                <li key={line.lineId} className="flex flex-col gap-0.5">
                  <p className="font-script line-clamp-2 text-sm">{line.text}</p>
                  <p className="text-ink-500 dark:text-ink-400 text-xs">
                    {line.sceneName} · missed {line.misses}{' '}
                    {pluralize(line.misses, 'time')}
                  </p>
                </li>
              ))}
            </ul>
            {current ? (
              <LinkButton to={`/rehearse/${current.showId}/missed`} variant="secondary" block>
                Drill missed lines
              </LinkButton>
            ) : null}
          </Card>
        ) : null}
      </Screen>
    </>
  );
}
